import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../utils/errorMessage';

// Public buy page for a business's price list item: /p/:slug/buy/:item.
// No sign-in. The buyer enters a name and email (and a term for a plan whose
// setup fee has term discounts) and pays on Stripe Checkout on the business's
// own Stripe account. A hidden field and the fill time help stop bots; a
// Cloudflare Turnstile check shows when REACT_APP_TURNSTILE_SITE_KEY is set.
function apiBase() {
  const { hostname, origin, protocol } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!local) return `${origin}/api/buy`;
  if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/buy`;
  return `${protocol}//${hostname}:5000/api/buy`;
}

const money = (n, c) => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: (c || 'usd').toUpperCase() }).format(Number(n) || 0); } catch (e) { return `$${Number(n || 0).toFixed(2)}`; }
};
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export default function BuyItem() {
  const { slug, item } = useParams();
  const location = useLocation();
  const justPaid = new URLSearchParams(location.search).get('paid') === '1';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', website: '', term: '' });
  const [human, setHuman] = useState('');
  const started = useRef(Date.now());
  const widget = useRef(null);
  const base = `${apiBase()}/${encodeURIComponent(slug)}/${encodeURIComponent(item)}`;

  useEffect(() => {
    axios.get(base).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e, 'This page was not found.')));
  }, [base]);

  useEffect(() => {
    if (!SITE_KEY || !data || !data.can_buy || justPaid) return undefined;
    const render = () => { if (window.turnstile && widget.current && !widget.current.dataset.done) { widget.current.dataset.done = '1'; window.turnstile.render(widget.current, { sitekey: SITE_KEY, callback: setHuman }); } };
    if (window.turnstile) { render(); return undefined; }
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'; s.async = true; s.onload = render;
    document.head.appendChild(s);
    return undefined;
  }, [data, justPaid]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await axios.post(`${base}/checkout`, { name: form.name, email: form.email, company: form.company, website: form.website,
        term_months: form.term || null, started_at: started.current, human_token: human || undefined });
      window.location.assign(r.data.url);
    } catch (err) { setError(formatApiError(err, 'Payment could not start. Please try again.')); setBusy(false); }
  };

  const terms = (data && data.item.terms) || [];
  const chosen = terms.find((t) => String(t.months || '') === form.term);
  const total = data ? Number(data.item.price) + (chosen ? Number(chosen.setup_fee) : (data.item.setup_fee ? Number(data.item.setup_fee.price) : 0)) : 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow p-6 w-full max-w-md">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4" role="alert">{error}</div>}
        {!data && !error && <p className="text-gray-600">Loading...</p>}
        {data && (
          <>
            {data.business.logo_url && <img src={data.business.logo_url} alt="" className="h-12 mb-2" />}
            <h1 className="text-xl font-semibold">{data.business.name}</h1>
            <div className="mt-4 border-t pt-4 space-y-1 text-sm">
              <p className="text-lg font-medium">{data.item.name}</p>
              {data.item.description && <p className="text-gray-700 whitespace-pre-line">{data.item.description}</p>}
              <p className="text-2xl font-semibold mt-2">{money(data.item.price, data.item.currency)}{data.item.billing === 'monthly' ? ' / month' : ''}</p>
              {data.item.setup_fee && <p>Plus {data.item.setup_fee.name}: {money(data.item.setup_fee.price, data.item.currency)}, billed with the first month{terms.length > 1 ? ' (less with a longer term)' : ''}.</p>}
            </div>
            {justPaid ? <p className="mt-4 text-green-700 font-medium">Thank you! Your payment is being processed. A receipt will be emailed to you.</p>
              : data.can_buy ? (
                <form onSubmit={submit} className="mt-4 space-y-2">
                  <input className="block w-full border rounded px-2 py-1" aria-label="Your name" placeholder="Your name" required maxLength={120} value={form.name} onChange={set('name')} />
                  <input className="block w-full border rounded px-2 py-1" type="email" aria-label="Email" placeholder="Email" required maxLength={255} value={form.email} onChange={set('email')} />
                  <input className="block w-full border rounded px-2 py-1" aria-label="Company (optional)" placeholder="Company (optional)" maxLength={200} value={form.company} onChange={set('company')} />
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }} value={form.website} onChange={set('website')} />
                  {terms.length > 1 && (
                    <select className="block w-full border rounded px-2 py-1" aria-label="Term" value={form.term} onChange={set('term')}>
                      {terms.map((t) => <option key={t.months || 'm2m'} value={t.months || ''}>{t.label}: setup fee {Number(t.setup_fee) > 0 ? money(t.setup_fee, data.item.currency) : 'waived'}</option>)}
                    </select>
                  )}
                  {SITE_KEY && <div ref={widget} />}
                  <p className="text-sm">Due today: <strong>{money(total, data.item.currency)}</strong></p>
                  <button type="submit" disabled={busy || (SITE_KEY && !human)} className="w-full bg-blue-600 text-white rounded py-3 font-semibold disabled:opacity-50">
                    {busy ? 'Opening secure payment...' : 'Continue to payment'}
                  </button>
                  {data.item.billing === 'monthly' && <p className="text-xs text-gray-500">Your card is saved securely by Stripe for the monthly payments.</p>}
                </form>
              ) : <p className="mt-4 text-gray-700">{data.note || 'Online purchase is not available.'}{data.business.email ? ` Contact ${data.business.email}.` : ''}</p>}
            <p className="mt-6 text-xs text-gray-400">Payments are processed securely by Stripe.</p>
          </>
        )}
      </div>
    </div>
  );
}
